import { CartItemType } from "../../services/redux/types/CartLoyaltyV2Types";
import {
  OrderCartV2Addon,
  OrderCartV2Line,
  OrderCartV2ResponseLine,
} from "../../services/redux/types/OrderCartV2Types";
import { ItemDetailsType, SubItemType } from "../types";
import { LoyaltyLine } from "./LoyaltyLine";
import {
  MenuLookups,
  emptyMenuLookups,
  ensureAddonGroupOnSubItem,
  resolveAddonNameFromLookups,
  resolveSelectedAddonFromLine,
} from "./menuLookup";

function buildItemDetailsFromSubItem(
  subItem: SubItemType,
  lineItemId: string
): ItemDetailsType {
  const parentItem = subItem.item;

  return {
    id: parentItem?.id ?? subItem.itemId ?? 0,
    img_url: parentItem?.img_url ?? "",
    item_name: parentItem?.item_name ?? subItem.name ?? "",
    originalPrice: subItem.price,
    uuid: parentItem?.uuid ?? lineItemId,
    customVatPercentage: parentItem?.customVatPercentage,
  };
}

function fallbackSubItem(line: OrderCartV2Line): SubItemType {
  return {
    uuid: line.subItemId,
    id: 0,
    name: "",
    price: line.unitPrice,
    basePrice: line.unitPrice,
    previousPrice: line.unitPrice,
    customFranchiseSubItemPrices: [],
    allAddonGroups: [],
  };
}

/** Applies flat API addons onto a menu sub-item template for edit/display. */
export function applyAddonsToSubItem(
  baseSubItem: SubItemType,
  lineAddons?: OrderCartV2Addon[],
  menuLookups: MenuLookups = emptyMenuLookups
): SubItemType {
  const templates: SubItemType = JSON.parse(JSON.stringify(baseSubItem));
  const subItem: SubItemType = JSON.parse(JSON.stringify(baseSubItem));

  if (!subItem.allAddonGroups) {
    subItem.allAddonGroups = [];
  }

  subItem.allAddonGroups.forEach((group) => {
    group.addonGroupSubItem = [];
  });

  if (!lineAddons?.length) {
    return subItem;
  }

  for (const addon of lineAddons) {
    const group = ensureAddonGroupOnSubItem(
      subItem,
      addon.addonGroupId,
      templates,
      menuLookups
    );
    if (!group) {
      continue;
    }

    const selected = resolveSelectedAddonFromLine(
      addon,
      templates,
      menuLookups
    );
    if (!selected) {
      continue;
    }

    if (selected.subItem) {
      // The resolved template clone carries the menu's full nested-options
      // tree; rebuild it so the display tree holds only *selected* nested
      // addons at every level (empty groups when the line has none).
      selected.subItem = applyAddonsToSubItem(
        selected.subItem,
        addon.nestedAddons,
        menuLookups
      );
    }

    if (!group.addonGroupSubItem) {
      group.addonGroupSubItem = [];
    }
    group.addonGroupSubItem.push(selected);
  }

  return subItem;
}

type OrderLineDisplayOptions = {
  responseLine?: OrderCartV2ResponseLine;
  pendingOfferApplicationId?: string;
  isPendingOfferLine?: boolean;
};

export function orderLineToDisplayItem(
  line: OrderCartV2Line,
  menuLookups: MenuLookups,
  options?: OrderLineDisplayOptions
): CartItemType {
  const responseLine = options?.responseLine;
  const baseSubItem =
    menuLookups.subItemsByUuid[line.subItemId] ?? fallbackSubItem(line);
  const subItemDetails = applyAddonsToSubItem(
    baseSubItem,
    line.addons,
    menuLookups
  );
  const itemDetails = buildItemDetailsFromSubItem(subItemDetails, line.itemId);
  // Real menu item version (for outdated-item detection); 0 is the valid default.
  const version = subItemDetails.item?.version ?? 0;

  const isOfferLinked = options?.isPendingOfferLine || undefined;

  if (responseLine) {
    return {
      ...LoyaltyLine.fromResponseLine(
        responseLine,
        subItemDetails,
        itemDetails,
        version
      ).toDisplayItem(),
      isOfferLinked,
    };
  }

  const pendingOfferApplicationId = options?.pendingOfferApplicationId;
  const appliedOffer =
    line.appliedOffer ??
    (options?.isPendingOfferLine && pendingOfferApplicationId
      ? {
          offerApplicationId: pendingOfferApplicationId,
          quantity: line.quantity,
        }
      : undefined);

  return {
    lineId: line.lineId,
    subItemDetails,
    itemDetails,
    note: line.note ?? "",
    count: line.quantity,
    totalPrice: line.unitPrice * line.quantity,
    version,
    priceWithoutOffer: line.unitPriceBeforeDiscount,
    appliedOffer,
    isOfferLinked,
  };
}

export function orderLinesToDisplayItems(
  lines: OrderCartV2Line[],
  menuLookups: MenuLookups,
  responseLines?: OrderCartV2ResponseLine[],
  offerLineIds?: string[],
  pendingOfferApplicationId?: string
): CartItemType[] {
  const responseByLineId = new Map(
    (responseLines ?? []).map((responseLine) => [
      responseLine.lineId,
      responseLine,
    ])
  );
  const pendingOfferLineIds = new Set(offerLineIds ?? []);

  return lines.map((line) =>
    orderLineToDisplayItem(line, menuLookups, {
      responseLine: line.lineId ? responseByLineId.get(line.lineId) : undefined,
      pendingOfferApplicationId,
      isPendingOfferLine: line.lineId
        ? pendingOfferLineIds.has(line.lineId)
        : false,
    })
  );
}

function enrichAddonDisplayName(
  addon: OrderCartV2Addon,
  menuLookups: MenuLookups
): OrderCartV2Addon {
  const nestedAddons = addon.nestedAddons?.map((nested) =>
    enrichAddonDisplayName(nested, menuLookups)
  );
  const enriched = nestedAddons ? { ...addon, nestedAddons } : addon;

  if (enriched.displayName?.trim()) {
    return enriched;
  }

  const displayName = resolveAddonNameFromLookups(
    enriched.subItemId,
    menuLookups
  );

  return displayName ? { ...enriched, displayName } : enriched;
}

export function enrichOrderLineAddonsFromLookups(
  lines: OrderCartV2Line[],
  menuLookups: MenuLookups = emptyMenuLookups
): OrderCartV2Line[] {
  return lines.map((line) => ({
    ...line,
    addons: line.addons?.map((addon) =>
      enrichAddonDisplayName(addon, menuLookups)
    ),
  }));
}

export function responseLinesToOrderLines(
  responseLines: OrderCartV2ResponseLine[],
  existingLines: OrderCartV2Line[]
): OrderCartV2Line[] {
  const existingByLineId = new Map(
    existingLines
      .filter((line) => line.lineId)
      .map((line) => [line.lineId!, line])
  );
  const existingBySubItem = new Map(
    existingLines.map((line) => [line.subItemId, line])
  );

  return responseLines.map((response) => {
    const existing =
      existingByLineId.get(response.lineId) ??
      existingBySubItem.get(response.subItemId);

    return {
      lineId: response.lineId,
      itemId: response.itemId,
      subItemId: response.subItemId,
      quantity: response.quantity,
      unitPrice: response.unitPrice,
      note: response.note ?? existing?.note,
      // Client-owned like `addons`: the response does not echo it, and losing
      // it would drop the line to the chain rate on the next calculate.
      customTaxPercentage: existing?.customTaxPercentage,
      addons: existing?.addons,
      unitPriceBeforeDiscount: existing?.unitPriceBeforeDiscount,
      appliedOffer: response.appliedOffer ?? existing?.appliedOffer,
    };
  });
}
